// Chatbot API Service
// API endpoint URL - can be set via environment variable or default to localhost
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface ChatResponse {
  response: string;
  conversation_id?: string;
}

export interface HealthCheckResponse {
  status: string;
  message?: string;
}

/**
 * Send a chat message to the API
 * @param query - The user's message
 * @param conversationId - Optional conversation ID for managing multiple conversations
 * @returns Promise with the bot's response
 */
export const sendChatMessage = async (
  query: string,
  conversationId?: string
): Promise<ChatResponse> => {
  try {
    const response = await fetch(`${API_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        conversation_id: conversationId,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error sending chat message:', error);
    throw error;
  }
};

/**
 * Clear chat history for a conversation
 * @param conversationId - The conversation ID to clear
 * @returns Promise with the clear response
 */
export const clearChatHistory = async (
  conversationId: string
): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_URL}/chat/clear?conversation_id=${conversationId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error clearing chat history:', error);
    throw error;
  }
};

/**
 * Health check to verify API is running
 * @returns Promise with health check response
 */
export const healthCheck = async (): Promise<HealthCheckResponse> => {
  try {
    const response = await fetch(`${API_URL}/`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error checking API health:', error);
    throw error;
  }
};

